import { useCallback, useEffect, useState } from 'react';
import { gymsService } from '../../services/gyms';
import { cragsService } from '../../services/crags';
import { trophiesService } from '../../services/trophies';
import { competitionsService } from '../../services/competitions';
import { useGymAdmin } from '../context/gym-admin-context';
import type { ManagedLocationType } from '../nav';

/** A place a competition hangs off, as far as a page needs to name it. */
export interface HostingPlace {
  name: string;
  slug: string;
}

/** The identity of the place being managed, whichever kind it is. */
export interface CurrentLocation {
  id: string;
  name: string;
  slug: string;
  type: ManagedLocationType;
  /**
   * What a competition stands behind — its circuit and the gym that hosts it —
   * whose staff run it too. Absent on every other kind of place.
   */
  trophy?: HostingPlace | null;
  venue?: HostingPlace | null;
}

/**
 * Fetches the selected place's identity from the endpoint that serves its kind.
 * Each is scoped to its own STI subclass server-side, so asking the wrong one
 * is a 404 rather than a wrong answer.
 */
async function fetchLocation(slug: string, type: ManagedLocationType) {
  switch (type) {
    case 'crag':
      return cragsService.getCragBySlug(slug);
    case 'trophy':
      return trophiesService.getTrophyBySlug(slug);
    case 'competition':
      return competitionsService.getCompetitionBySlug(slug);
    default:
      return gymsService.getGymBySlug(slug);
  }
}

/**
 * The active selection reduced to what a section shared by several kinds of
 * place needs — its id, mostly, for the endpoints keyed on `Location` rather
 * than on one subclass (staff, gallery images, the roster, the kanban). Pages
 * that edit fields belonging to one kind want the full record instead:
 * `useCurrentGym` / `useCurrentCrag` / `useCurrentTrophy`.
 */
export function useCurrentLocation() {
  const { selectedGymSlug, locationType } = useGymAdmin();
  const [location, setLocation] = useState<CurrentLocation | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (slug: string, type: ManagedLocationType) => {
    setLoading(true);
    try {
      const record = await fetchLocation(slug, type);
      const hangsOff =
        type === 'competition' && 'trophy' in record
          ? { trophy: record.trophy ?? null, venue: record.venue ?? null }
          : {};
      setLocation({ id: record.id, name: record.name, slug: record.slug, type, ...hangsOff });
    } catch {
      setLocation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedGymSlug) {
      setLocation(null);
      setLoading(false);
      return;
    }
    load(selectedGymSlug, locationType);
  }, [selectedGymSlug, locationType, load]);

  return { location, loading, slug: selectedGymSlug, locationType };
}
